/*
 * Manual payment-email sends (A-52): the outbox "Send again" and the Overview "Send payment
 * breakdown". Pure: mode wording, selection, eligibility, request bodies and the dry-run / result
 * shaping. The JSX renders only what these return. Unit-tested in logic.test.js.
 */
import { EM_DASH, api_error } from '../format';
import { reason_error, trim_reason } from '../needs_attention/params';

const str = (v) => (typeof v === 'string' ? v : '');
const list = (v) => (Array.isArray(v) ? v.map(str).filter(Boolean) : []);
const int = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// payemail.TestInboxes / the payments inbox: the fallback when a response does not name them.
export const DEFAULT_TEST_INBOXES = ['kuldeep@myyogateacher.com', 'nithun@myyogateacher.com'];
export const DEFAULT_PAYMENTS_INBOX = 'teachers_payment@myyogateacher.com';
export const MAX_SEND = 100;

// ---- mode ----

// Anything but an explicit LIVE is TEST: a send must never claim to reach a teacher by default.
export const send_mode = (v) => (str(v) === 'LIVE' ? 'LIVE' : 'TEST');

// The confirm step's banner, worded as the A-52 contract fixes it.
export const mode_banner = (mode, test_inboxes, payments_inbox) => {
  if (send_mode(mode) === 'LIVE') {
    return `Goes to the teacher, copy to ${str(payments_inbox) || DEFAULT_PAYMENTS_INBOX}`;
  }
  const inboxes = list(test_inboxes);
  const shown = (inboxes.length ? inboxes : DEFAULT_TEST_INBOXES).join(', ');
  return `Live mode is off: this email goes to the test inboxes (${shown}), not to the teacher.`;
};

// The persistent badge on the outbox page and beside the breakdown action.
export const mode_badge = (mode) =>
  send_mode(mode) === 'LIVE'
    ? { mode: 'LIVE', tone: 'orange', text: 'Live mode: emails go to teachers' }
    : { mode: 'TEST', tone: 'blue', text: 'Test mode: emails go to test inboxes' };

// GET /email/switch -> the mode a manual send runs in now (null until the read lands).
export const mode_of_switch = (data) => {
  if (!data || typeof data.live_enabled !== 'boolean') return null;
  return {
    mode: send_mode(data.delivery_mode),
    test_inboxes: list(data.test_inboxes),
    payments_inbox: str(data.payments_inbox)
  };
};

// ---- outbox selection ----

// An email still queued or being handed over is not sent again: the worker owns it.
const IN_FLIGHT = new Set(['PENDING', 'SENDING']);

export const outbox_resend_state = (row) => {
  if (!row || !(row.id > 0)) return { selectable: false, reason: 'No email to send again.' };
  if (IN_FLIGHT.has(row.status)) {
    return { selectable: false, reason: 'Still sending: wait until it is sent or fails.' };
  }
  return { selectable: true, reason: '' };
};

// Picks are {id: true}; ids stay picked across "Older emails" pages until cleared.
export const toggle_id = (picked, id) => {
  const next = { ...picked };
  if (next[id]) delete next[id];
  else next[id] = true;
  return next;
};

const selectable_ids = (rows) =>
  (rows || []).filter((r) => outbox_resend_state(r).selectable).map((r) => r.id);

export const toggle_all_ids = (picked, rows) => {
  const ids = selectable_ids(rows);
  const all = ids.length > 0 && ids.every((id) => picked[id]);
  const next = { ...picked };
  ids.forEach((id) => {
    if (all) delete next[id];
    else next[id] = true;
  });
  return next;
};

export const all_pick_state = (picked, rows) => {
  const ids = selectable_ids(rows);
  const n = ids.filter((id) => picked[id]).length;
  return {
    all: ids.length > 0 && n === ids.length,
    some: n > 0 && n < ids.length,
    any: ids.length > 0
  };
};

export const picked_ids = (picked) =>
  Object.keys(picked || {})
    .filter((k) => picked[k])
    .map(Number)
    .filter((n) => n > 0)
    .sort((a, b) => a - b);

export const selection_error = (n) => {
  if (n < 1) return 'Select at least one email.';
  if (n > MAX_SEND) return `Select at most ${MAX_SEND} emails at a time.`;
  return '';
};

// ---- breakdown eligibility (Overview) ----

const NOT_VERIFIED_REASON = {
  OPEN: 'Not verified yet: this month is still open.',
  REVIEW: 'Not verified yet: this month is still in review.'
};

export const NOT_PAID_HERE_REASON =
  'Not paid through Teacher Payments (payroll, test or staff account), so there is no breakdown to send.';

// Only a verified (closed) teacher-month paid here has a breakdown; not-paid-here wins, as in goserver breakdownRefusal.
export const breakdown_eligibility = (row) => {
  if (row && (str(row.not_priced_reason) || str(row.not_priced_text))) {
    return { enabled: false, reason: NOT_PAID_HERE_REASON };
  }
  const lc = str(row && row.lifecycle);
  if (lc === 'CLOSED') return { enabled: true, reason: '' };
  return {
    enabled: false,
    reason: NOT_VERIFIED_REASON[lc] || 'Not verified yet: no closed month for this teacher.'
  };
};

// The bulk button: picks on other pages are unknown here, so they count as possibly verified.
export const bulk_breakdown_state = (uuids, rows) => {
  if (!uuids.length) return { enabled: false, reason: 'Select at least one teacher.' };
  if (uuids.length > MAX_SEND) {
    return { enabled: false, reason: `Select at most ${MAX_SEND} teachers at a time.` };
  }
  const by_uuid = {};
  (rows || []).forEach((r) => {
    by_uuid[r.teacher_uuid] = r;
  });
  const any = uuids.some((u) => !by_uuid[u] || breakdown_eligibility(by_uuid[u]).enabled);
  return any
    ? { enabled: true, reason: '' }
    : {
        enabled: false,
        reason:
          'None of the selected teachers has a breakdown to send: not verified yet or not paid here.'
      };
};

// ---- requests ----

const force_part = (force, reason) => (force ? { force: true, reason: trim_reason(reason) } : {});

// The real send names the dry run's mode; goserver refuses (409 EMAIL_MODE_CHANGED) if the switch moved since.
const send_part = (dry_run, expected_mode) => {
  if (dry_run) return { dry_run: true };
  return expected_mode ? { expected_mode: send_mode(expected_mode) } : {};
};

// POST /email/outbox/send; dry_run: the plan the confirm step shows first.
export const outbox_send_body = (
  ids,
  { dry_run = false, force = false, reason = '', expected_mode = '' } = {}
) => ({
  outbox_ids: ids.slice(),
  ...send_part(dry_run, expected_mode),
  ...force_part(force, reason)
});

// POST /email/breakdown/send; target: {all: true} or {teacher_uuids: [...]}.
export const breakdown_send_body = (
  pay_month,
  target,
  { dry_run = false, force = false, reason = '', expected_mode = '' } = {}
) => ({
  pay_month,
  ...(target && target.all
    ? { all: true }
    : { teacher_uuids: (target.teacher_uuids || []).slice() }),
  ...send_part(dry_run, expected_mode),
  ...force_part(force, reason)
});

export const CODE_MODE_CHANGED = 'EMAIL_MODE_CHANGED';
export const MODE_CHANGED_TEXT =
  'The email mode (test or live) changed since the preview. Nothing was sent. Preview again to see who gets it now.';
const SEND_FAILED_TEXT = 'The send did not go through. Nothing was sent; try again.';

// A failed real send: a mode change makes the plan stale (preview again); anything else shows the server's sentence.
export const send_failure_view = (e) => {
  const { code, message } = api_error(e);
  if (code === CODE_MODE_CHANGED) return { mode_changed: true, text: MODE_CHANGED_TEXT };
  return { mode_changed: false, text: message || SEND_FAILED_TEXT };
};

// ---- dry run and results ----

// Warnings a reason can override; NOT_VERIFIED never can.
export const FORCEABLE = ['SUPERSEDED', 'ALREADY_SENT_LIVE'];

// Codes as payemail/manual.go names them (warnings + refusals); the server's message wins over this text.
export const WARNINGS = {
  SUPERSEDED: {
    label: 'Newer email exists',
    text: 'A newer email replaces this one (for example the details were approved or rejected since, or the month was reopened).'
  },
  ALREADY_SENT_LIVE: {
    label: 'Already sent to teacher',
    text: 'This email already went to the teacher (or is queued to go).'
  },
  NOT_VERIFIED: {
    label: 'Not verified',
    text: "This teacher's month is not verified (closed) yet, so its payment breakdown cannot be sent."
  },
  NOT_PAID_HERE: {
    label: 'Not paid here',
    text: 'Not paid through Teacher Payments (payroll, test or staff account), so there is no breakdown to send.'
  },
  NOT_FOUND: { label: 'Not found', text: 'Not found. Reload the page and pick it again.' },
  NOT_SENDABLE: {
    label: 'Cannot be built',
    text: 'This email cannot be built from the current data. Fix the month first.'
  },
  NOT_RESENDABLE: {
    label: 'Cannot send again',
    text: 'This email cannot be sent again from here.'
  },
  NO_ADDRESS: {
    label: 'No email address',
    text: "Not sent: this teacher has no valid email address. Fix it on the teacher's profile."
  }
};

const warning_view = (code, message) => {
  if (!code) return null;
  const known = WARNINGS[code];
  return {
    code,
    label: known ? known.label : code,
    text: str(message) || (known ? known.text : ''),
    forceable: FORCEABLE.indexOf(code) !== -1
  };
};

const GOES = new Set(['WOULD_SEND', 'QUEUED', 'PENDING', 'SENT', 'SENDING']);

// ready: goes on Send (a forced warning still shows); held: goes only when forced; blocked: never.
const plan_bucket = (w, status) => {
  if (GOES.has(status)) return 'ready';
  if (w) return w.forceable ? 'held' : 'blocked';
  return status === 'REFUSED' ? 'blocked' : 'ready';
};

// The row's one code: the refusal code, else the first warning (contract `warning`, server `code`/`warnings`).
const row_code = (r) => str(r.code) || str(r.warning) || list(r.warnings)[0] || '';

// payemail Kind.Label(); the send rows carry the kind code only.
export const KIND_LABELS = {
  PAYMENT_DETAILS_SUBMITTED: 'Payment details submitted',
  PAYMENT_DETAILS_APPROVED: 'Payment details approved',
  PAYMENT_DETAILS_REJECTED: 'Payment details rejected',
  INVOICE_CONFIRMED: 'Invoice confirmed',
  PAYMENT_SENT: 'Payment sent',
  INVOICE_REMINDER: 'Invoice reminder',
  PAYMENT_BREAKDOWN: 'Payment breakdown'
};

// Send rows carry teacher_name (goserver A-52); the uuid shows only for an unnamed row.
const names_of = (r) => ({
  teacher_name: str(r.teacher_name) || str(r.teacher_uuid) || EM_DASH,
  kind_label: str(r.kind_label) || KIND_LABELS[str(r.kind)] || str(r.kind)
});

const row_view = (r, i) => {
  const w = warning_view(row_code(r), r.message);
  const status = str(r.status);
  return {
    key: `${i}-${int(r.id) || str(r.teacher_uuid) || str(r.ref)}`,
    id: int(r.id),
    new_id: int(r.new_id),
    teacher_uuid: str(r.teacher_uuid),
    ...names_of(r),
    pay_month: str(r.pay_month),
    mode: send_mode(r.mode),
    to_display: str(r.to_display),
    status,
    warning: w,
    bucket: plan_bucket(w, status)
  };
};

const count_of = (rows, bucket) => rows.filter((r) => r.bucket === bucket).length;

// The dry run (dry_run: true on either POST): rows, the mode, and the first email's preview when sent.
export const plan_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r, i) => row_view(r || {}, i));
  const p = d.preview && typeof d.preview === 'object' ? d.preview : null;
  return {
    mode: send_mode(d.mode),
    test_inboxes: list(d.test_inboxes),
    payments_inbox: str(d.payments_inbox),
    rows,
    counts: {
      total: rows.length,
      ready: count_of(rows, 'ready'),
      held: count_of(rows, 'held'),
      blocked: count_of(rows, 'blocked')
    },
    preview: p,
    // The first email that would go, for the preview (a ready row, else a held one).
    first: rows.find((r) => r.bucket === 'ready') || rows.find((r) => r.bucket === 'held') || null
  };
};

// "3 go now · 1 held back · 2 not sendable": every bucket named, zeros included.
export const plan_counts_text = (c) =>
  `${plural(c.ready, 'email goes', 'emails go')} now · ${c.held} held back (needs a reason) · ${c.blocked} cannot be sent`;

export const send_count = (plan, force) => plan.counts.ready + (force ? plan.counts.held : 0);

export const send_label = (n) =>
  n > 0 ? `Send ${plural(n, 'email', 'emails')}` : 'Nothing to send';

// The confirm form's blocking message ('' = Send is allowed).
export const confirm_error = (plan, force, reason) => {
  if (!plan) return 'The plan has not loaded yet.';
  if (force && plan.counts.held) {
    const e = reason_error(reason);
    if (e) return e;
  }
  if (send_count(plan, force) === 0) {
    return plan.counts.held
      ? 'Every email here is held back. Tick "Send anyway" and give a reason, or cancel.'
      : 'Nothing here can be sent.';
  }
  return '';
};

const SENT_STATUSES = { QUEUED: 'Queued', PENDING: 'Queued', SENT: 'Sent', SENDING: 'Sending' };

// One result row: sent/queued with the new outbox id, or refused with why.
export const result_row = (r, i = 0) => {
  const v = row_view(r || {}, i);
  const label = SENT_STATUSES[v.status];
  if (label && v.new_id) {
    return {
      ...v,
      outcome: 'sent',
      outcome_label: label,
      tone: 'green',
      note: `New email #${v.new_id}`
    };
  }
  const why = v.warning
    ? `${v.warning.label}: ${v.warning.text}`
    : str(r && r.message) || 'Refused.';
  return { ...v, outcome: 'refused', outcome_label: 'Refused', tone: 'red', note: why };
};

export const results_view = (data, planned_mode) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r, i) => result_row(r, i));
  const sent = rows.filter((r) => r.outcome === 'sent').length;
  const refused = rows.length - sent;
  const mode = send_mode(d.mode || (rows[0] && rows[0].mode));
  return {
    rows,
    sent,
    refused,
    mode,
    // The switch moved between the plan and the send: say where the emails really went.
    mode_changed: !!planned_mode && rows.length > 0 && mode !== send_mode(planned_mode),
    summary: `${plural(sent, 'email', 'emails')} queued, ${refused} refused.`
  };
};

// The notification after a send (Hridaya has success/error only).
export const results_notice = (v) => ({
  type: v.sent === 0 && v.refused > 0 ? 'error' : 'success',
  text: `${v.summary} ${v.mode === 'LIVE' ? 'Sent to teachers.' : 'Sent to the test inboxes only.'}`
});
